import { useState, useRef } from 'react';
import { createSession, getSessionMessages } from '../services/sessionService';
import { generateSmartTitle } from '../utils/messageUtils';

const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useSessionManagement = () => {
  const [sessionId, setSessionId] = useState(null);
  const [loadingSessionMessages, setLoadingSessionMessages] = useState(false);
  const [sessionMessagesPage, setSessionMessagesPage] = useState(null);
  const [sessionTotalPages, setSessionTotalPages] = useState(null);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const sessionIdRef = useRef(null);
  const refreshSessionsRef = useRef(null);
  const messagesLimit = 50;

  const createNewSession = async (input) => {
    const smartTitle = generateSmartTitle(input);
    const sessionResponse = await createSession(smartTitle);
    if (sessionResponse?.sessionId) {
      setSessionId(sessionResponse.sessionId);
      sessionIdRef.current = sessionResponse.sessionId;
      
      if (refreshSessionsRef.current && typeof refreshSessionsRef.current === 'function') {
        try {
          refreshSessionsRef.current();
        } catch (error) {
          console.error('Error refreshing sessions:', error);
        }
      }
      return sessionResponse.sessionId;
    } else {
      throw new Error('Failed to create session');
    }
  };

  const formatMessagesFromResponse = (messages) => {
    const formattedMessages = [];
    const sortedMessages = [...messages].sort((a, b) => {
      const timeA = a.created_at?.seconds || new Date(a.created_at).getTime() / 1000 || 0;
      const timeB = b.created_at?.seconds || new Date(b.created_at).getTime() / 1000 || 0;
      return timeA - timeB;
    });

    let pendingToolCalls = [];
    
    sortedMessages.forEach((msg, index) => {
      try {
        if (msg.role === 'user') {
          formattedMessages.push({
            id: generateMessageId(),
            text: msg.content || '',
            sender: 'user'
          });
        } else if (msg.role === 'assistant') {
          if (msg.tool_calls) {
            try {
              const toolCalls = typeof msg.tool_calls === 'string' 
                ? JSON.parse(msg.tool_calls) 
                : msg.tool_calls;

              if (Array.isArray(toolCalls)) {
                toolCalls.forEach(toolCall => {
                  if (toolCall.function) {
                    const toolName = toolCall.function.name || 'unknown_tool';
                    let description = '';
                    
                    if (toolCall.function.arguments) {
                      try {
                        const args = typeof toolCall.function.arguments === 'string'
                          ? JSON.parse(toolCall.function.arguments)
                          : toolCall.function.arguments;
                        
                        description = args.user_description || args.query || '';
                      } catch (e) {
                        console.error(`[Session Load] Error parsing tool arguments:`, e);
                      }
                    }
                    
                    pendingToolCalls.push({
                      tool_name: toolName,
                      description: description
                    });
                  }
                });
              }
            } catch (e) {
              console.error(`[Session Load] Error parsing tool_calls:`, e);
            }
          }
          
          const hasActualContent = msg.content && msg.content.trim();
          
          if (hasActualContent) {
            const hasThinking = msg.content.includes('</think>');

            const aiMessage = {
              id: generateMessageId(),
              text: msg.content,
              sender: 'ai',
              messageId: msg.message_id,
              toolUse: [...pendingToolCalls]
            };

            if (hasThinking) {
              const parts = msg.content.split('</think>');
              aiMessage.thinkingContent = parts[0] || '';
              aiMessage.responseContent = parts.length > 1 ? parts[1] : '';
              aiMessage.isThinking = false;
            }
            
            formattedMessages.push(aiMessage);
            pendingToolCalls = [];
          }
        }
      } catch (error) {
        console.error(`[Session Load] Error processing message:`, error);
      }
    });

    return formattedMessages;
  };

  const loadSessionMessages = async (selectedSessionId, setMessages, setFeedbackGiven, setCopyFeedback, scrollToBottom) => {
    setLoadingSessionMessages(true);
    try {
      const response = await getSessionMessages(selectedSessionId, null, messagesLimit);

      if (response?.data) {
        const totalCount = response.data.total_count || 0;
        const currentPage = response.data.page || 1;
        const calculatedTotalPages = Math.ceil(totalCount / messagesLimit);
        
        setSessionTotalPages(calculatedTotalPages);
        setSessionMessagesPage(currentPage);

        if (response.data.messages) {
          const formattedMessages = formatMessagesFromResponse(response.data.messages);
          
          setSessionId(selectedSessionId);
          sessionIdRef.current = selectedSessionId;
          setMessages(formattedMessages);
          
          setFeedbackGiven({});
          setCopyFeedback({});

          setTimeout(scrollToBottom, 100);
        } else {
          setSessionId(selectedSessionId);
          sessionIdRef.current = selectedSessionId;
          setMessages([]);
          setFeedbackGiven({});
          setCopyFeedback({});
        }
      }
    } catch (error) {
      console.error('[Session Load] Error loading session:', error);
    } finally {
      setLoadingSessionMessages(false);
    }
  };

  const loadOlderMessages = async (setMessages) => {
    if (isLoadingOlderMessages || !sessionMessagesPage || sessionMessagesPage <= 1) {
      return;
    }

    setIsLoadingOlderMessages(true);
    try {
      const previousPage = sessionMessagesPage - 1;
      const response = await getSessionMessages(sessionIdRef.current, previousPage, messagesLimit);

      if (response?.data?.messages) {
        const formattedMessages = formatMessagesFromResponse(response.data.messages);
        
        setMessages(prevMessages => [...formattedMessages, ...prevMessages]);
        setSessionMessagesPage(previousPage);
      }
    } catch (error) {
      console.error('[Session Load] Error loading older messages:', error);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  const handleNewSession = (setMessages, setFeedbackGiven, setCopyFeedback) => {
    setSessionId(null);
    sessionIdRef.current = null;
    setSessionMessagesPage(null);
    setSessionTotalPages(null);
    setMessages([]);
    setFeedbackGiven({});
    setCopyFeedback({});
  };

  return {
    sessionId,
    setSessionId,
    sessionIdRef,
    loadingSessionMessages,
    isLoadingOlderMessages,
    sessionMessagesPage,
    sessionTotalPages,
    refreshSessionsRef,
    createNewSession,
    loadSessionMessages,
    loadOlderMessages,
    handleNewSession
  };
};

